/**
 * BandPrimitive — lightweight-charts v5 series primitive that paints a
 * translucent filled band between an upper and lower price curve.
 *
 * We use primitives instead of stacked AreaSeries because AreaSeries fills
 * down to baseline 0, overflowing the chart on markets with large absolute
 * prices (NIFTY ≈ 24k, BTCUSD ≈ 60k). A primitive draws only between the
 * two curves — no overflow.
 *
 * Time units: UNIX seconds (IST-shifted, matching the chart's time axis).
 */
import type {
  ISeriesPrimitive,
  IPrimitivePaneView,
  IPrimitivePaneRenderer,
  SeriesAttachedParameter,
  Time,
  IChartApi,
  ISeriesApi,
  SeriesType,
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

export interface BandPoint {
  time: number;      // UNIX seconds, IST-shifted (same scale as candle data)
  upper: number;
  lower: number;
  /** Round 94 — optional per-point fill color (rgba/hex, opacity baked in).
   *  When ANY point sets this, the band is drawn segment-by-segment using the
   *  left point's color (directional bands: green above EMA, red below).
   *  When unset everywhere, the band falls back to a single `opts.fillColor`. */
  color?: string;
}

export interface BandOptions {
  fillColor: string;   // rgba/hex — opacity should already be baked in
  lineColor?: string;  // if set, the upper and lower edges are stroked
  lineWidth?: number;  // default 1
  /** Dash pattern for the stroked edges, in CSS pixels (scaled to the bitmap).
   *  Used to say "this part can still move": the CLN HTF band draws its closed
   *  bars solid and the still-forming bar dashed, so a repainting segment is
   *  never mistaken for a settled one. Omit for a solid edge (the default, and
   *  what every pre-existing call-site gets). */
  lineDash?: number[];
  /** Draw the fill (and stroked edges) as a STAIRCASE — the value holds until
   *  the next point's time, then steps — so the fill hugs stepped line series
   *  (lineType WithSteps) exactly. Default false = straight segments.
   *
   *  NOTE: `step` and per-point `color` do NOT compose. The directional branch
   *  in the renderer draws one quad per segment from the RAW coordinates and
   *  never calls `stepped()`. A caller that needs both a staircase and
   *  per-segment colours must instead split its points into consecutive
   *  same-direction runs and attach one BandPrimitive per run — the pattern the
   *  CPR / C-POC / CLN-HTF bands use. */
  step?: boolean;
  /** Paint order relative to the host series. Defaults to lightweight-charts'
   *  'normal' (the historical behaviour of every existing call-site — do not
   *  change that default). 'bottom' puts the fill BEHIND the candles, which is
   *  what a context band wants so it never competes with price. */
  zOrder?: 'bottom' | 'normal' | 'top';
  /** Floor the drawn thickness at this many CSS pixels, expanded around the
   *  band's midpoint. Same reasoning as LrBodyPrimitive's MIN_BODY_PX: a band
   *  whose two curves nearly touch is still carrying information about WHERE it
   *  is, and at 1px it reads as absent rather than as flat. Measured: a 1D
   *  regression body on a 60m chart came out ~5px tall across the whole pane,
   *  which at a context band's opacity was invisible.
   *  Off by default — existing call-sites draw true height, unchanged. */
  minHeightPx?: number;
  // Round 40 — projection support. lightweight-charts' timeToCoordinate()
  // returns null for any time past the last REAL bar in the series. For
  // projection bands that live in the rightOffset whitespace, set these
  // two fields and the renderer falls back to bar-spacing arithmetic:
  //   x = lastBarX + ((pointTime - lastBarTime) / barSpacingSec) * pxPerBar
  // No candle-series whitespace manipulation needed.
  lastBarTime?: number;    // UNIX sec of the last real candle on the chart
  barSpacingSec?: number;  // seconds between consecutive bars (chart interval)
}

class BandRenderer implements IPrimitivePaneRenderer {
  constructor(
    private readonly points: BandPoint[],
    private readonly opts: BandOptions,
    private readonly chart: IChartApi,
    private readonly series: ISeriesApi<SeriesType>,
  ) {}

  draw(target: CanvasRenderingTarget2D): void {
    const pts = this.points;
    if (pts.length < 2) return;
    const ts = this.chart.timeScale();
    const series = this.series;

    // Round 40 — set up the bar-spacing fallback for projection points that
    // sit past the last real bar (timeToCoordinate returns null there).
    const { lastBarTime, barSpacingSec } = this.opts;
    let lastBarX: number | null = null;
    let pxPerBar = 0;
    if (lastBarTime != null && barSpacingSec && barSpacingSec > 0) {
      const lx = ts.timeToCoordinate(lastBarTime as Time);
      if (lx != null) {
        lastBarX = Number(lx);
        try { pxPerBar = (ts.options() as any).barSpacing || 0; } catch { pxPerBar = 0; }
      }
    }
    const resolveX = (t: number): number | null => {
      const x = ts.timeToCoordinate(t as Time);
      if (x != null) return Number(x);
      // Past the last real bar — fall back to bar-spacing arithmetic.
      if (lastBarX != null && pxPerBar > 0 && lastBarTime != null && barSpacingSec) {
        const barsAhead = (t - lastBarTime) / barSpacingSec;
        return lastBarX + barsAhead * pxPerBar;
      }
      return null;
    };

    const directional = pts.some(p => p.color != null);
    target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      // Resolve screen coords for every point, skipping any that fall off-range.
      const upperCoords: Array<{ x: number; y: number }> = [];
      const lowerCoords: Array<{ x: number; y: number }> = [];
      const colors: string[] = [];
      // Applied in BITMAP space, so it is a constant on screen at any DPR.
      const minH = Math.max(0, Number(this.opts.minHeightPx) || 0) * vpr;
      for (const p of pts) {
        const x = resolveX(p.time);
        if (x == null) continue;
        const yU = series.priceToCoordinate(p.upper);
        const yL = series.priceToCoordinate(p.lower);
        if (yU == null || yL == null) continue;
        let top = Number(yU) * vpr;
        let bot = Number(yL) * vpr;
        // priceToCoordinate is y-DOWN, so the higher PRICE is the smaller y.
        // Normalise before flooring or the expansion goes the wrong way for a
        // caller that passes upper/lower the other way round.
        if (top > bot) { const t = top; top = bot; bot = t; }
        if (minH > 0 && bot - top < minH) {
          const mid = (top + bot) / 2;
          top = mid - minH / 2;
          bot = mid + minH / 2;
        }
        upperCoords.push({ x: x * hpr, y: top });
        lowerCoords.push({ x: x * hpr, y: bot });
        colors.push(p.color || this.opts.fillColor);
      }
      if (upperCoords.length < 2) return;

      // Staircase: expand a coord list into stepped corners (hold y[i-1] until
      // x[i], then rise to y[i]) — mirrors lightweight-charts' WithSteps lines.
      const step = this.opts.step === true;
      const stepped = (coords: Array<{ x: number; y: number }>): Array<{ x: number; y: number }> => {
        if (!step) return coords;
        const out: Array<{ x: number; y: number }> = [coords[0]];
        for (let i = 1; i < coords.length; i++) {
          out.push({ x: coords[i].x, y: coords[i - 1].y });
          out.push({ x: coords[i].x, y: coords[i].y });
        }
        return out;
      };

      if (directional) {
        // Per-segment quads, each filled with the left point's color so the
        // band changes color exactly where the line crosses its companion.
        for (let i = 0; i < upperCoords.length - 1; i++) {
          ctx.beginPath();
          ctx.moveTo(upperCoords[i].x, upperCoords[i].y);
          ctx.lineTo(upperCoords[i + 1].x, upperCoords[i + 1].y);
          ctx.lineTo(lowerCoords[i + 1].x, lowerCoords[i + 1].y);
          ctx.lineTo(lowerCoords[i].x, lowerCoords[i].y);
          ctx.closePath();
          ctx.fillStyle = colors[i];
          ctx.fill();
        }
      } else {
        // Single filled polygon: upper curve left→right, then lower right→left
        // (stepped when `step`, so the fill matches staircase lines).
        const up = stepped(upperCoords);
        const lo = stepped(lowerCoords);
        ctx.beginPath();
        ctx.moveTo(up[0].x, up[0].y);
        for (let i = 1; i < up.length; i++) ctx.lineTo(up[i].x, up[i].y);
        for (let i = lo.length - 1; i >= 0; i--) ctx.lineTo(lo[i].x, lo[i].y);
        ctx.closePath();
        ctx.fillStyle = this.opts.fillColor;
        ctx.fill();
      }

      if (this.opts.lineColor) {
        ctx.lineWidth = Math.max(1, (this.opts.lineWidth || 1) * Math.min(hpr, vpr));
        ctx.strokeStyle = this.opts.lineColor;
        // Dashes are given in CSS px, so scale them into bitmap space like the
        // line width above, or the pattern shrinks on a 2x display.
        const dash = this.opts.lineDash;
        if (dash && dash.length) ctx.setLineDash(dash.map(d => d * Math.min(hpr, vpr)));
        const up = stepped(upperCoords);
        const lo = stepped(lowerCoords);
        // Upper edge
        ctx.beginPath();
        ctx.moveTo(up[0].x, up[0].y);
        for (let i = 1; i < up.length; i++) ctx.lineTo(up[i].x, up[i].y);
        ctx.stroke();
        // Lower edge
        ctx.beginPath();
        ctx.moveTo(lo[0].x, lo[0].y);
        for (let i = 1; i < lo.length; i++) ctx.lineTo(lo[i].x, lo[i].y);
        ctx.stroke();
        // The canvas is shared with every other primitive on this pane — leave
        // the dash state as we found it or the next one inherits it.
        if (dash && dash.length) ctx.setLineDash([]);
      }
    });
  }
}

class BandPaneView implements IPrimitivePaneView {
  constructor(private readonly parent: BandPrimitive) {}
  // 'normal' is lightweight-charts' own default for a pane view that declares
  // no zOrder, so every existing call-site keeps the exact paint order it had.
  zOrder(): 'bottom' | 'normal' | 'top' {
    return this.parent.opts.zOrder || 'normal';
  }
  renderer(): IPrimitivePaneRenderer | null {
    const chart = this.parent.chart;
    const series = this.parent.series;
    if (!chart || !series) return null;
    return new BandRenderer(this.parent.points, this.parent.opts, chart, series);
  }
}

export class BandPrimitive implements ISeriesPrimitive<Time> {
  points: BandPoint[] = [];
  opts: BandOptions;
  chart: IChartApi | null = null;
  series: ISeriesApi<SeriesType> | null = null;
  private readonly _paneViews: BandPaneView[];

  constructor(points: BandPoint[], opts: BandOptions) {
    this.points = points;
    this.opts = opts;
    this._paneViews = [new BandPaneView(this)];
  }

  attached(params: SeriesAttachedParameter<Time>): void {
    this.chart = params.chart as IChartApi;
    this.series = params.series as ISeriesApi<SeriesType>;
  }
  detached(): void {
    this.chart = null;
    this.series = null;
  }

  updateAllViews(): void { /* data stored on instance; views read it fresh each draw */ }
  paneViews(): readonly IPrimitivePaneView[] { return this._paneViews; }

  setData(points: BandPoint[]): void {
    this.points = points;
    this.chart?.applyOptions({}); // nudge redraw
  }
}
